/* eslint-disable @typescript-eslint/no-empty-object-type */
import { useCustomToast } from '@/hooks/use-custom-toast';
import React, { memo, useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import {
  createLabelChatValidationSchema,
  TCreateLabelChatValidation,
  useCreateLabelChatMutation,
} from '../../../api/create-label-chat.api';
import { zodResolver } from '@hookform/resolvers/zod';
import { axiosExtractErrorMessage } from '@/lib/axios';
import { queryClient } from '@/lib/react-query';
import { GET_LIST_LABEL_CHAT_QUERY_KEY } from '../../../api/get-list-label-chat.api';
import { useCaseModuleModalRootActionsStore } from '@/features/cases/_shared/components/case-modal-root/case-modal-root.store';
import { handleDomBubbleEvent } from '@/utils/dom';
// import { GENERAL_LABEL_COLOR } from '@/features/settings/label/_shared/constants/general-label-color';
import { GENERAL_LABEL_COLOR } from '@/features/cases/_shared/constants/cases.contants';
import { cn } from '@/utils/cn';
import { Input } from '@/components/_ui/input';
import {
  Modal,
  ModalHeader,
  ModalTitle,
  ModalContent,
  ModalFooter,
  ModalConfirmButton,
  ModalCloseButton,
} from '@/components/_ui/modal';
import { Spinner } from '@/components/_ui/spinner';

export type TCreateLabelChatModalType = 'CreateLabelChatModal';
export interface ICreateLabelChatModalProps {}
const CreateLabelChatModal = memo((_props: ICreateLabelChatModalProps) => {
  const caseModuleModalRootActions = useCaseModuleModalRootActionsStore();
  const isOpenCreateLabelChatModal = useMemo(() => {
    return caseModuleModalRootActions.checkIsOpenModal('CreateLabelChatModal');
  }, [caseModuleModalRootActions]);
  const showToast = useCustomToast();
  const form = useForm<TCreateLabelChatValidation>({
    resolver: zodResolver(createLabelChatValidationSchema),
    mode: 'onChange',
    defaultValues: {
      name: '',
      color: '',
    },
  });

  const mutationCreateLabelChat = useCreateLabelChatMutation({
    mutationConfigOptions: {
      onError: (_error) => {
        const errorMessage = axiosExtractErrorMessage(_error) || 'Error adding label chat, please try again';
        showToast({
          message: errorMessage,
          type: 'error',
        });
      },
      onSuccess: () => {
        Promise.all([queryClient.invalidateQueries({ queryKey: [GET_LIST_LABEL_CHAT_QUERY_KEY] })]);
        caseModuleModalRootActions.closeModal('CreateLabelChatModal');
        form.reset();
        showToast({
          message: 'Label chat created successfully',
          type: 'success',
        });
      },
    },
  });

  const handleSubmitCreateLabelChat = useCallback(
    (data: TCreateLabelChatValidation) => {
      mutationCreateLabelChat.mutate({
        name: data?.name,
        color: data?.color,
      });
    },
    [mutationCreateLabelChat]
  );

  const handleClose = useCallback(() => {
    caseModuleModalRootActions.closeModal('CreateLabelChatModal');
    form.reset();
  }, [caseModuleModalRootActions, form]);

  return (
    <Modal isOpen={isOpenCreateLabelChatModal} lockBodyScroll onClose={handleClose} className="p-[32px] w-[500px]">
      <ModalHeader>
        <ModalTitle>Add Label Chat</ModalTitle>
      </ModalHeader>
      <ModalContent>
        <form
          onSubmit={(e) => {
            handleDomBubbleEvent(e);
            form.handleSubmit(handleSubmitCreateLabelChat)(e);
          }}
          id="create-label-chat-form"
          className="grid gap-[16px]"
        >
          <div className="grid gap-[4px]">
            <label htmlFor="name-label-chat" className="text-[#4B4B4B] text-[14px] font-normal">
              Name*
            </label>
            <Input id="name-label-chat" placeholder="Enter Label Chat Name" {...form.register('name')} />
            {form.formState.errors.name && (
              <p className="text-red-500 text-[12px] font-normal">{form.formState.errors.name.message}</p>
            )}
          </div>

          <div className="grid gap-[4px]">
            <label htmlFor="color-label-chat" className="text-[#4B4B4B] text-[14px] font-normal">
              Color*
            </label>
            <div id="color-label-chat" className="grid grid-cols-3 gap-[22px]">
              {Object.entries(GENERAL_LABEL_COLOR).map(([key, color], index) => {
                const isSelected = form.watch('color') === color;
                return (
                  <button
                    type="button"
                    onClick={(e) => {
                      handleDomBubbleEvent(e);
                      form.setValue('color', color);
                    }}
                    key={index + 1}
                    className={cn(
                      'w-full gap-[6px] flex justify-start py-[8px] px-[6px] rounded-[8px] bg-[#F1F5F9] cursor-pointer border-transparent border-2 hover:border-[#0C8DBF] hover:bg-[#0C8DBF]/20 transition-all duration-300',
                      isSelected && 'border-[#0C8DBF] bg-[#0C8DBF]/20'
                    )}
                  >
                    <div className="w-[16px] h-[16px] rounded-full " style={{ backgroundColor: color }}></div>
                    <span className="text-[#25151E] text-[12px] font-normal">{key}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </form>
      </ModalContent>
      <ModalFooter>
        <ModalCloseButton
          onClick={(e) => {
            handleDomBubbleEvent(e);
            handleClose();
          }}
        >
          Cancel
        </ModalCloseButton>
        <ModalConfirmButton form="create-label-chat-form" type="submit" disabled={mutationCreateLabelChat.isPending}>
          {mutationCreateLabelChat.isPending ? <Spinner isLoading={true} className="w-[18px] h-[18px]" /> : null} Save
        </ModalConfirmButton>
      </ModalFooter>
    </Modal>
  );
});
CreateLabelChatModal.displayName = 'CreateLabelChatModal';
export default CreateLabelChatModal;
